'use client';

import React, { ReactNode } from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { FallbackComponent } from '@/app/error';

/**
 * Resolve the Bugsnag release stage from env. Priority:
 *   1. NEXT_PUBLIC_RELEASE_STAGE  — explicit override
 *   2. NEXT_PUBLIC_VERCEL_ENV     — 'production' | 'preview' | 'development' on Vercel
 *   3. NODE_ENV                   — 'production' for built bundles, 'development' under `next dev`
 */
function resolveReleaseStage(): string {
  const explicit = process.env.NEXT_PUBLIC_RELEASE_STAGE;
  if (explicit) return explicit;
  const vercel = process.env.NEXT_PUBLIC_VERCEL_ENV;
  if (vercel) return vercel === 'preview' ? 'staging' : vercel;
  return process.env.NODE_ENV === 'production' ? 'production' : 'development';
}

/**
 * Errors we never want to send to Bugsnag. These are browser noise, not
 * application bugs, and reporting them just wastes dashboard space.
 */
const IGNORED_ERROR_PATTERNS: RegExp[] = [
  // Benign layout race — a ResizeObserver callback triggered more layout
  // than could finish in one frame. Chrome itself debates whether to fire
  // this at all; there is no user-visible problem and no application bug.
  // See https://stackoverflow.com/q/49384120
  /^ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i,
  // Cross-origin script errors lose their message and just say "Script error"
  // — they're almost always from browser extensions and never actionable.
  /^Script error\.?$/i,
  // Another extension / cross-origin artefact.
  /^Non-Error promise rejection captured$/i,
];

function isIgnoredErrorMessage(message: string): boolean {
  return IGNORED_ERROR_PATTERNS.some(re => re.test(message));
}

let started = false;
function startBugsnag() {
  if (started) return;
  // Guard to avoid running in SSR or during pre-render
  if (typeof window === 'undefined') return;
  Bugsnag.start({
    apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY || 'd5f2b61a7a1bf87a22b296299d79e740',
    plugins: [new BugsnagPluginReact()],
    appVersion: process.env.APP_VERSION || 'dev',
    releaseStage: resolveReleaseStage(),
    // Only send reports from deployed environments — dev errors stay out of
    // the dashboard and off the pager.
    enabledReleaseStages: ['production', 'staging'],
    onError: (event) => {
      // Drop known-benign browser noise before it hits the dashboard.
      const messages = event.errors.map(e => e.errorMessage || '');
      if (messages.some(isIgnoredErrorMessage)) return false;
      return true;
    },
  });
  started = true;
}
startBugsnag();

const plugin = Bugsnag.getPlugin('react');
if (!plugin) {
  // Soft-fail on client if plugin missing
  console.warn('[bugsnag] React plugin not available');
}

const ErrorBoundary = plugin?.createErrorBoundary(React);

export default function BugsnagBoundary({ children }: { children: ReactNode }) {
  // If plugin didn’t load, just render children.
  if (!ErrorBoundary) return <>{children}</>;
  return <ErrorBoundary FallbackComponent={FallbackComponent}>{children}</ErrorBoundary>;
}